import { supabase } from '@/integrations/supabase/client';
import {
  type PaidAdCopyGenerationRequest,
  PaidAdCopyGenerationRequestSchema,
} from '@/lib/paid-media/copy-assistant';
import {
  PaidAdCopySuggestionRunErrorSchema,
  type PaidAdCopySuggestionRunResponse,
  PaidAdCopySuggestionRunResponseSchema,
} from '@/lib/paid-media/copy-suggestion-run-contract';

export class PaidAdCopySuggestionServiceError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number | null,
  ) {
    super(message);
    this.name = 'PaidAdCopySuggestionServiceError';
  }
}

async function accessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) {
    throw new PaidAdCopySuggestionServiceError(
      'unauthorized',
      'Sign in before generating paid Ad copy.',
      401,
    );
  }
  return data.session.access_token;
}

export class PaidAdCopySuggestionService {
  async generate(input: PaidAdCopyGenerationRequest): Promise<PaidAdCopySuggestionRunResponse> {
    const request = PaidAdCopyGenerationRequestSchema.safeParse(input);
    if (!request.success) {
      throw new PaidAdCopySuggestionServiceError(
        'validation_failed',
        'Use an exact paid Ad draft revision and copy target.',
        400,
      );
    }
    const response = await fetch('/api/paid-ad/copy-suggestions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await accessToken()}`,
      },
      body: JSON.stringify(request.data),
    });
    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new PaidAdCopySuggestionServiceError(
        'invalid_response',
        'The paid Ad copy service returned an invalid response.',
        response.status,
      );
    }
    if (!response.ok) {
      const error = PaidAdCopySuggestionRunErrorSchema.safeParse(payload);
      if (error.success) {
        throw new PaidAdCopySuggestionServiceError(
          error.data.code,
          error.data.error,
          error.data.status ?? response.status,
        );
      }
      throw new PaidAdCopySuggestionServiceError(
        'invalid_response',
        'The paid Ad copy suggestion could not be generated.',
        response.status,
      );
    }
    const parsed = PaidAdCopySuggestionRunResponseSchema.safeParse(payload);
    if (!parsed.success) {
      throw new PaidAdCopySuggestionServiceError(
        'invalid_response',
        'The paid Ad copy service returned an invalid response.',
        response.status,
      );
    }
    return parsed.data;
  }
}

export const paidAdCopySuggestionService = new PaidAdCopySuggestionService();
